import React, {
    ReactElement,
    cloneElement,
    isValidElement,
    useEffect,
    useRef,
    useState,
} from "react";
import { twMerge } from "tailwind-merge";

interface CopyTooltipProps {
    children: ReactElement<React.HTMLAttributes<HTMLElement>>;
    resetDelay?: number;
    clickDelay?: number;
    className?: string;
    onCopyClick?: (event: React.MouseEvent<HTMLElement>, count: number) => void;
}

const messages = [
    "Copiado!",
    "Cópia dupla!",
    "Cópia tripla!",
    "Você gosta mesmo de copiar",
    "Super cópia!!",
    "Cópia nível expert!",
    "Cópia insana!!",
    "Cópia transcendental!!",
    "Cópia apocalíptica!!!",
    "Cópia Divina!!!",
];
export function CopyTooltip({
    children,
    resetDelay = 3000,
    clickDelay = 500,
    className,
    onCopyClick,
}: CopyTooltipProps) {
    const [count, setCount] = useState(0);
    const [visible, setVisible] = useState(false);
    const [shake, setShake] = useState(false);

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const shakeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clickLockRef = useRef(false);

    const last = messages.length;

    function getText(count: number) {
        if (count <= 0) return "";
        return messages[Math.min(count, last) - 1];
    }

    function triggerShake() {
        setShake(false);
        requestAnimationFrame(() => setShake(true));

        if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
        shakeTimeoutRef.current = setTimeout(() => setShake(false), 350);
    }

    function handleClick(e: React.MouseEvent<HTMLElement>) {
        if (clickLockRef.current) return;

        clickLockRef.current = true;
        setTimeout(() => {
            clickLockRef.current = false;
        }, clickDelay);

        setCount((prev) => {
            const newCount = prev + 1;

            setVisible(true);

            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                setVisible(false);
                setCount(0);
                setShake(false);
            }, resetDelay);

            if (newCount >= last) triggerShake();

            onCopyClick?.(e, newCount);

            return newCount;
        });

        children.props.onClick?.(e);
    }

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
        };
    }, []);

    if (!isValidElement(children)) return null;

    const childWithProps = cloneElement(children, { onClick: handleClick });

    return (
        <div className={twMerge('relative inline-flex w-fit', className)}>
            {childWithProps}
            <div
                className={twMerge(
                    `absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-md text-xs font-medium bg-gray-900 text-white shadow-lg transition-all duration-200 whitespace-nowrap ${visible
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-95 pointer-events-none"
                    } ${shake ? "crazy-tooltip" : ""}`
                )}
            >
                {getText(count)}
                <div className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
            </div>
        </div>
    );
}